/**
 * Centralized React Query keys for the Circles feature.
 *
 * Same rationale as `src/features/breakfast/queryKeys.ts`: every cross-feature
 * invalidation site currently uses raw string literals (`["circles_schedule"]`,
 * `["circle-members", userId]`, etc.), which means:
 *   • A typo (`"circles_schedule"` vs `"circle_schedule"`) silently fails —
 *     the mutation reports success, but no consumer refetches.
 *   • Renaming a key requires a full-codebase grep and an all-hands code review.
 *   • There's no `readonly`-tagged tuple to narrow TypeScript checks on the
 *     key arity (user-scoped vs global).
 *
 * Exporting typed constants makes all three problems vanish at compile-time.
 * Every Circles consumer and every cross-feature invalidator imports from here.
 */

// ─── Root tokens ──────────────────────────────────────────────────────────
//
// These string values MUST match every existing query-key in the codebase
// EXACTLY — changing them would orphan the cache. If a rename is ever wanted,
// do it here and grep for stragglers.
export const CIRCLES_SCHEDULE_ROOT        = "circles_schedule"       as const;
export const CIRCLES_SCHEDULE_RADAR_ROOT  = "circles_schedule_radar" as const;
export const CIRCLE_MEMBERS_ROOT          = "circle-members"         as const;
export const CIRCLE_INVITES_SENT_ROOT     = "circle-invites-sent"    as const;
export const CIRCLE_AUDIT_ROOT            = "circle-audit"           as const;
export const CIRCLE_SCHEDULE_WIDE_ROOT    = "circle-schedule-wide"   as const;
export const CIRCLE_PEER_SCOPES_ROOT      = "circle-peer-scopes"     as const;

// ─── Prefix keys (use with `invalidateQueries` for fan-out) ───────────────

/** Global circles schedule feed — one entry per (auth, rangeStart, rangeEnd). */
export const circlesSchedulePrefix = [CIRCLES_SCHEDULE_ROOT] as const;

/** Per-user 30-day radar (see `SuggestedDayBanner`). */
export const circlesScheduleRadarPrefix = [CIRCLES_SCHEDULE_RADAR_ROOT] as const;

/** Per-user accepted circle members list. */
export const circleMembersPrefix = [CIRCLE_MEMBERS_ROOT] as const;

/** Per-user pending outbound invites. */
export const circleInvitesSentPrefix = [CIRCLE_INVITES_SENT_ROOT] as const;

/** Per-user circle audit log. */
export const circleAuditPrefix = [CIRCLE_AUDIT_ROOT] as const;

/** Per-user 30-day wide schedule (home-page "next office day" widget). */
export const circleScheduleWidePrefix = [CIRCLE_SCHEDULE_WIDE_ROOT] as const;

/** Per-user map of accepted peers → their own share scope (mutual chip). */
export const circlePeerScopesPrefix = [CIRCLE_PEER_SCOPES_ROOT] as const;

// ─── Full per-user keys (use for `useQuery` declarations) ─────────────────

export function circleMembersKey(userId: string | null | undefined) {
  return [CIRCLE_MEMBERS_ROOT, userId ?? null] as const;
}

export function circleInvitesSentKey(userId: string | null | undefined) {
  return [CIRCLE_INVITES_SENT_ROOT, userId ?? null] as const;
}

export function circleAuditKey(userId: string | null | undefined) {
  return [CIRCLE_AUDIT_ROOT, userId ?? null] as const;
}

export function circleScheduleWideKey(userId: string | null | undefined) {
  return [CIRCLE_SCHEDULE_WIDE_ROOT, userId ?? null] as const;
}

export function circlePeerScopesKey(userId: string | null | undefined) {
  return [CIRCLE_PEER_SCOPES_ROOT, userId ?? null] as const;
}

export function circlesScheduleRadarKey(
  userId: string | null | undefined,
  anchorDate: string,
) {
  return [CIRCLES_SCHEDULE_RADAR_ROOT, userId ?? null, anchorDate] as const;
}

export function circlesScheduleKey(
  isAuthenticated: boolean,
  rangeStart: string,
  rangeEnd: string,
) {
  return [CIRCLES_SCHEDULE_ROOT, isAuthenticated, rangeStart, rangeEnd] as const;
}
